import * as React from 'react';
import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { Button } from '../../../../components/ui/Button';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../components/ui/Dialog';
import { FormField, Input } from '../../../../components/ui/FormField';
import { FormSelect } from '../../../../components/ui/FormSelect';
import { OrgUnitPicker } from '../../../../features/org/components/OrgUnitPicker';
import { useToast } from '../../../../components/ui/Toast';
import { Shield, MapPin, Key, UserMinus, UserCheck, Trash2, Monitor, LogOut } from 'lucide-react';
import { getSession } from '../../../../lib/session';
import { usePageTitle } from '../../../../lib/hooks';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { DataView } from '../../../../components/ui/DataView';
import {
  useStaffDetail,
  useRoles,
  useUpdateStaffRole,
  useUpdateStaffBranch,
  useResetStaffPassword,
  useUpdateStaffStatus,
  useDeleteStaff,
  useStaffSessions,
  useRevokeStaffSession,
  type StaffSession,
} from '../../../../features/staff/network/staff.api';
import { useOrgUnits } from '../../../../features/org/network/org.api';
import { TempPasswordDialog } from '../../../../features/staff/components/TempPasswordDialog';

export const Route = createFileRoute('/_auth/admin/staff/$id')({
  beforeLoad: ({ location }) => {
    const session = getSession();
    const isAllowed =
      session?.user.type === 'group_admin' ||
      session?.user.type === 'company_admin' ||
      session?.permissions.includes('admin.staff.read');
    if (!isAllowed) {
      throw redirect({ to: '/', search: { callbackUrl: location.href } });
    }
  },
  component: StaffDetailPage,
});

function StaffDetailPage() {
  const { id } = Route.useParams();
  usePageTitle('Staff Member Details');
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: member, isLoading, error } = useStaffDetail(id);
  const { data: roles } = useRoles();
  const { data: orgUnits } = useOrgUnits();
  const { data: sessions, isLoading: sessionsLoading, isError: sessionsError, error: sessionsErrorObj, refetch: refetchSessions } = useStaffSessions(id);
  const revokeSessionMutation = useRevokeStaffSession(id);

  // Mutations
  const roleMutation = useUpdateStaffRole();
  const branchMutation = useUpdateStaffBranch();
  const resetMutation = useResetStaffPassword();
  const statusMutation = useUpdateStaffStatus();
  const deleteMutation = useDeleteStaff();

  // Dialog / Popover states
  const [roleDialogOpen, setRoleDialogOpen] = React.useState(false);
  const [branchDialogOpen, setBranchDialogOpen] = React.useState(false);
  const [resetDialogOpen, setResetDialogOpen] = React.useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [sessionToRevoke, setSessionToRevoke] = React.useState<StaffSession | null>(null);
  const [tempPasswordToShow, setTempPasswordToShow] = React.useState<string | null>(null);

  // Form states
  const [selectedRoleId, setSelectedRoleId] = React.useState('');
  const [selectedCompanyWide, setSelectedCompanyWide] = React.useState(false);
  const [selectedOrgUnitId, setSelectedOrgUnitId] = React.useState('');

  React.useEffect(() => {
    if (member) {
      setSelectedRoleId(member.roleId ?? '');
      setSelectedCompanyWide(member.companyWide ?? false);
      setSelectedOrgUnitId(member.orgUnitId ?? '');
    }
  }, [member]);

  const handleUpdateRole = async () => {
    if (!selectedRoleId) return;
    try {
      await roleMutation.mutateAsync({ userId: id, roleId: selectedRoleId, companyWide: selectedCompanyWide });
      toast({
        title: 'Role Updated',
        description: 'Staff member role has been successfully updated.',
        type: 'success',
      });
      setRoleDialogOpen(false);
    } catch (err) {
      console.error(err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to update role';
      toast({
        title: 'Error',
        description: errorMsg,
        type: 'error',
      });
    }
  };

  const handleUpdateBranch = async () => {
    if (!selectedOrgUnitId) return;
    try {
      await branchMutation.mutateAsync({ userId: id, orgUnitId: selectedOrgUnitId });
      toast({
        title: 'Branch Updated',
        description: 'Staff member branch has been successfully updated.',
        type: 'success',
      });
      setBranchDialogOpen(false);
    } catch (err) {
      console.error(err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to update branch';
      toast({
        title: 'Error',
        description: errorMsg,
        type: 'error',
      });
    }
  };

  const handleResetPassword = async () => {
    try {
      const res = await resetMutation.mutateAsync(id);
      setResetDialogOpen(false);
      if (res.tempPassword) {
        setTempPasswordToShow(res.tempPassword);
      } else {
        toast({
          title: 'Success',
          description: `Password reset initiated for ${member?.name}.`,
          type: 'success',
        });
      }
    } catch (err) {
      console.error(err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to initiate password reset';
      toast({
        title: 'Error',
        description: errorMsg,
        type: 'error',
      });
    }
  };

  const handleToggleStatus = async () => {
    if (!member) return;
    const newStatus = member.status === 'suspended' ? 'active' : member.status === 'pending_activation' ? 'active' : 'suspended';
    try {
      await statusMutation.mutateAsync({ userId: id, status: newStatus });
      toast({
        title: 'Status Updated',
        description: `Staff member is now ${newStatus === 'active' ? 'Active' : 'Suspended'}.`,
        type: 'success',
      });
      setStatusDialogOpen(false);
    } catch (err) {
      console.error(err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to update status';
      toast({
        title: 'Error',
        description: errorMsg,
        type: 'error',
      });
    }
  };

  const handleDeleteStaff = async () => {
    try {
      await deleteMutation.mutateAsync(id);
      toast({
        title: 'Staff Deleted',
        description: 'Staff member has been successfully deleted.',
        type: 'success',
      });
      setDeleteDialogOpen(false);
      navigate({ to: '/admin/staff' });
    } catch (err) {
      console.error(err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to delete staff member';
      toast({
        title: 'Error',
        description: errorMsg,
        type: 'error',
      });
    }
  };

  const handleRevokeSession = async () => {
    if (!sessionToRevoke) return;
    try {
      await revokeSessionMutation.mutateAsync(sessionToRevoke.id);
      toast({
        title: 'Session revoked',
        description: `The device session for ${member?.name} has been signed out.`,
        type: 'success',
      });
      setSessionToRevoke(null);
    } catch (err) {
      console.error(err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to revoke session';
      toast({ title: 'Error', description: errorMsg, type: 'error' });
    }
  };

  const roleOptions = React.useMemo(() => {
    if (!roles) return [];
    return roles.map((r) => ({ value: r.id, label: r.name }));
  }, [roles]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Skeleton className="h-8 w-1/4" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Error loading staff member details. Make sure the ID is correct.
        </div>
      </div>
    );
  }

  let badgeStatus: 'success' | 'warning' | 'danger' | 'info' = 'info';
  let statusLabel = 'Unknown';
  if (member.status === 'active') {
    badgeStatus = 'success';
    statusLabel = 'Active';
  } else if (member.status === 'pending_password_reset') {
    badgeStatus = 'warning';
    statusLabel = 'Reset Needed';
  } else if (member.status === 'pending_activation') {
    badgeStatus = 'info';
    statusLabel = 'Pending Activation';
  } else if (member.status === 'suspended') {
    badgeStatus = 'danger';
    statusLabel = 'Suspended';
  }

  return (
    <div className="flex flex-col w-full h-full gap-6">
      <PageHeader title={member.name} subtitle="Manage profile details, assignments, and account status." backTo="/admin/staff" action={<StatusBadge status={badgeStatus} label={statusLabel} />} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Detail Card */}
        <div className="lg:col-span-2 bg-surface-1 border border-border rounded-[12px] p-6 shadow-sm space-y-6">
          <h2 className="text-lg font-semibold text-text-primary">Profile Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-medium text-text-muted uppercase tracking-wider mb-1">Email Address</label>
              <div className="text-sm font-medium text-text-primary">{member.email}</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted uppercase tracking-wider mb-1">Mobile Number</label>
              <div className="text-sm font-medium text-text-primary">{member.phoneNumber || 'Not provided'}</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted uppercase tracking-wider mb-1">Role / Permissions</label>
              <div className="text-sm font-medium text-text-primary flex items-center gap-1.5">
                <span>{member.roleName || 'None'}</span>
                {member.companyWide && (
                  <span className="text-[10px] font-bold uppercase not-italic bg-accent-600 text-white rounded-full px-1.5 py-0.5">
                    All branches
                  </span>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted uppercase tracking-wider mb-1">Assigned Branch</label>
              <div className="text-sm font-medium text-text-primary">{member.orgUnitName || 'None'}</div>
            </div>
          </div>
        </div>

        {/* Actions panel */}
        <div className="bg-surface-1 border border-border rounded-[12px] p-6 shadow-sm space-y-6">
          <h2 className="text-lg font-semibold text-text-primary">Administrative Actions</h2>
          <div className="flex flex-col gap-3">
            <Button
              variant="secondary"
              onClick={() => setRoleDialogOpen(true)}
              className="flex items-center justify-start gap-2 w-full text-left"
            >
              <Shield className="h-4 w-4" />
              Change Role
            </Button>

            <Button
              variant="secondary"
              onClick={() => setBranchDialogOpen(true)}
              className="flex items-center justify-start gap-2 w-full text-left"
            >
              <MapPin className="h-4 w-4" />
              Change Branch
            </Button>

            <Button
              variant="secondary"
              onClick={() => setResetDialogOpen(true)}
              className="flex items-center justify-start gap-2 w-full text-left"
            >
              <Key className="h-4 w-4" />
              Reset Password
            </Button>

            <Button
              variant="secondary"
              onClick={() => setStatusDialogOpen(true)}
              className="flex items-center justify-start gap-2 w-full text-left"
            >
              {member.status === 'suspended' ? (
                <>
                  <UserCheck className="h-4 w-4 text-emerald-500" />
                  Reactivate Staff
                </>
              ) : member.status === 'pending_activation' ? (
                <>
                  <UserCheck className="h-4 w-4 text-emerald-500" />
                  Activate Staff
                </>
              ) : (
                <>
                  <UserMinus className="h-4 w-4 text-amber-500" />
                  Deactivate Staff
                </>
              )}
            </Button>

            <Button
              variant="danger"
              onClick={() => setDeleteDialogOpen(true)}
              className="flex items-center justify-start gap-2 w-full text-left"
            >
              <Trash2 className="h-4 w-4" />
              Delete Staff
            </Button>
          </div>
        </div>
      </div>

      {/* Sessions panel */}
      <div className="bg-surface-1 border border-border rounded-[12px] p-6 shadow-sm space-y-4">
        <h2 className="text-lg font-semibold text-text-primary">Active Sessions</h2>
        <DataView
          query={{ isLoading: sessionsLoading, isError: sessionsError, error: sessionsErrorObj, data: sessions, refetch: refetchSessions }}
          emptyTitle="No active sessions"
          emptyDescription={`${member.name} has no active device sessions.`}
        >
          {(sessionList) => (
            <div className="flex flex-col gap-3">
              {sessionList.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-4 border border-border rounded-[8px] p-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <Monitor className="h-4 w-4 text-text-secondary shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-text-primary truncate">{s.userAgent || 'Unknown device'}</div>
                      <div className="text-xs text-text-secondary font-mono">{s.ipAddress || 'Unknown IP'}</div>
                      <div className="text-xs text-text-muted">Last active {new Date(s.updatedAt).toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSessionToRevoke(s)}
                    className="text-danger-500 hover:text-danger-600 shrink-0"
                  >
                    <LogOut className="h-4 w-4 mr-1.5" />
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DataView>
      </div>

      {/* Change Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <FormSelect
              label="Select New Role"
              required
              options={[{ value: '', label: 'Select role...' }, ...roleOptions]}
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
            />
            <label className="flex items-start gap-2 cursor-pointer pt-1">
              <Input
                type="checkbox"
                checked={selectedCompanyWide}
                onChange={(e) => setSelectedCompanyWide(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border text-accent-500 focus:ring-accent-500 cursor-pointer"
                disabled={roleMutation.isPending}
              />
              <span className="text-xs">
                <span className="font-semibold text-text-primary block">Company-wide access</span>
                <span className="text-text-secondary">
                  Lifts branch scoping so this role applies across all branches.
                </span>
              </span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setRoleDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateRole} disabled={!selectedRoleId} loading={roleMutation.isPending}>
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Branch Dialog */}
      <Dialog open={branchDialogOpen} onOpenChange={setBranchDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Branch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <FormField label="Select New Branch" required>
              <OrgUnitPicker
                nodes={orgUnits || []}
                value={selectedOrgUnitId}
                onChange={setSelectedOrgUnitId}
                unitType="BRANCH"
                placeholder="Select branch..."
              />
            </FormField>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setBranchDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateBranch} disabled={!selectedOrgUnitId} loading={branchMutation.isPending}>
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <ConfirmDialog
        open={resetDialogOpen}
        title="Reset Password?"
        description={`Are you sure you want to reset the password for ${member.name}? This will send a password reset link to ${member.email}.`}
        onConfirm={handleResetPassword}
        onCancel={() => setResetDialogOpen(false)}
        loading={resetMutation.isPending}
        confirmLabel="Reset"
      />

      {/* Toggle Status Dialog */}
      <ConfirmDialog
        open={statusDialogOpen}
        title={member.status === 'suspended' ? 'Reactivate Staff?' : member.status === 'pending_activation' ? 'Activate Staff?' : 'Deactivate Staff?'}
        description={`Are you sure you want to ${member.status === 'suspended' ? 'reactivate' : member.status === 'pending_activation' ? 'activate' : 'deactivate'} ${member.name}?`}
        onConfirm={handleToggleStatus}
        onCancel={() => setStatusDialogOpen(false)}
        loading={statusMutation.isPending}
        confirmLabel={member.status === 'suspended' ? 'Reactivate' : member.status === 'pending_activation' ? 'Activate' : 'Deactivate'}
      />

      {/* Delete Staff Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Staff?"
        // allow-raw-sql: plain English confirmation message ("delete", "from"), not SQL — heuristic false positive
        description={`Are you sure you want to delete ${member.name}? This is a soft-delete and will hide the staff profile from list views. All historical records remain intact.`}
        onConfirm={handleDeleteStaff}
        onCancel={() => setDeleteDialogOpen(false)}
        loading={deleteMutation.isPending}
        confirmLabel="Delete"
      />

      {/* Revoke Session Dialog */}
      <ConfirmDialog
        open={!!sessionToRevoke}
        title="Revoke session?"
        description={`Are you sure you want to sign out "${sessionToRevoke?.userAgent || 'this device'}" for ${member.name}?`}
        onConfirm={handleRevokeSession}
        onCancel={() => setSessionToRevoke(null)}
        loading={revokeSessionMutation.isPending}
        confirmLabel="Revoke"
      />

      {/* Explicit-dismiss Temporary Password Dialog (D-013) */}
      <TempPasswordDialog
        open={Boolean(tempPasswordToShow)}
        tempPassword={tempPasswordToShow || ''}
        userName={member?.name}
        title="Password Reset Successful"
        description="A new temporary password has been generated. Please copy and share it securely with the staff member. This password will not be displayed again."
        onClose={() => setTempPasswordToShow(null)}
      />
    </div>
  );
}
